import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import MetaTags from "react-meta-tags";
import jwt_decode from 'jwt-decode';
import {
    Row, Col, Card, CardBody, CardHeader,
    Label, Input,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axios from '../../api/axios';
import { ParkingLayoutModal } from './ModalForm/ModalForm';
import { Tooltip, OverlayTrigger, Popover } from 'react-bootstrap';
import '../parking.scss';

const ITEMS_PER_PAGE = 100;
const BASE_URL = '/parking_new/property';
const INITIAL_FILTER_OPTIONS = { parking_type: [], floor: [], city: [], status: [] };
const INITIAL_FILTER = { parking_type: '', floor: '', city: '', status: '' };
const INITIAL_STATS = { total: 0, activeProperties: 0, statistics: {} };

const LayoutSetup = () => {
    const [properties, setProperties] = useState([]);
    const currentPage = 1;
    const [filterData, setFilterData] = useState(INITIAL_FILTER);
    const [filterOptions, setFilterOptions] = useState(INITIAL_FILTER_OPTIONS);
    const [propertyStats, setPropertyStats] = useState(INITIAL_STATS);
    const [isFilterOpen, setIsFilterOpen] = useState(true);
    const [modalState, setModalState] = useState({ isOpen: false, property: null });

    const dispatch = useDispatch();

    const getUserFromToken = () => {
        const token = window.localStorage.getItem('accessToken');
        if (!token) return null;
        try { return jwt_decode(token); } catch { return null; }
    };

    const fetchProperties = async () => {
        const user = getUserFromToken();
        if (!user) return;
        try {
            dispatch({ type: "SET_LOADING", payload: true });
            const response = await axios.get(BASE_URL, {
                params: {
                    page: currentPage, limit: ITEMS_PER_PAGE,
                    role: user.role, domain: user.domain,
                    managerID: user.role === "manager" ? user.id : "",
                    filter: filterData,
                },
            });
            const { results, filters: options } = response.data;
            setProperties(results.properties || []);
            setPropertyStats({
                total: results.totalProperties || 0,
                activeProperties: results.totalActiveProperties || 0,
                totalPages: results.totalPages || 0,
                currentPage: results.currentPage || 1,
                statistics: results.statistics || {}
            });
            if (options) {
                setFilterOptions({
                    parking_type: options.parkingTypes || [],
                    floor: options.floors || [],
                    city: options.cities || [],
                    status: options.statuses || [],
                });
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        } finally {
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };

    useEffect(() => { fetchProperties(); }, [currentPage]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchProperties();
    };

    const handlePropertyAction = (property) => setModalState({ isOpen: true, property });
    const closeModal = () => setModalState({ isOpen: false, property: null });

    const handleParkingLayoutSubmit = async (values, propertyId, building_name) => {
        try {
            const { layouts } = values;
            await axios.post(`/parking_new/parking-layout`, { property_id: propertyId, building_name, values, layouts });
            fetchProperties();
        } catch (error) {
            console.error('Error saving parking layout:', error);
        }
    };

    const FIELD_LABELS = { parking_type: 'Parking Type', floor: 'Floor', city: 'City', status: 'Status' };

    const FilterSection = () => (
        isFilterOpen ? (
            <div className="filter-card card mt-2">
                <div className="card-body">
                    <form onSubmit={handleFilterSubmit}>
                        <div className="filter-grid">
                            {['parking_type', 'floor', 'city', 'status'].map((field) => (
                                <div key={field} className="filter-item">
                                    <label>{FIELD_LABELS[field]}</label>
                                    <Input
                                        type="select"
                                        name={field}
                                        value={filterData[field]}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        {filterOptions[field]?.map((opt) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </Input>
                                </div>
                            ))}
                            <div className="filter-actions">
                                <button type="submit" className="btn-parking" style={{ background: '#4361ee', color: '#fff', border: 'none', height: 36 }}>
                                    <i className="mdi mdi-magnify" /> Search
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        ) : null
    );

    const PropertiesTable = () => (
        <>
            <div className="table-responsive">
                <table className="table layout-setup-table table-bordered mb-0">
                    <thead>
                        <tr>
                            <th className="col-1">#</th>
                            <th className="col-2">Title</th>
                            <th className="col-2">Building</th>
                            <th className="col-2">Location</th>
                            <th className="col-1">Status</th>
                            <th className="col-1">Edit</th>
                            <th className="col-3">Layouts</th>
                        </tr>
                    </thead>
                    <tbody>
                        {properties.length > 0 ? (
                            properties.map((property, index) => (
                                <tr key={property.id || index}>
                                    <td>{index + 1}</td>
                                    <td><strong>{property.title}</strong></td>
                                    <td>{property.building_name || <span className="text-muted">—</span>}</td>
                                    <td>{property.location}</td>
                                    <td>
                                        <span className={`status-pill ${property.status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                                            {property.status}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => handlePropertyAction(property)}
                                            style={{ borderRadius: 6 }}
                                        >
                                            <FeatherIcon icon="edit" size={14} />
                                        </button>
                                    </td>
                                    <td>
                                        {property.layouts && property.layouts.length > 0 ? (
                                            property.layouts.map((layout, i) => (
                                                <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                                    <span className="layout-badge">{layout.parking_type}</span>
                                                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>Floor {layout.floor}</span>
                                                    <OverlayTrigger
                                                        trigger="hover"
                                                        placement="top"
                                                        overlay={
                                                            <Popover id={`popover-${i}`}>
                                                                <Popover.Body className="p-2" style={{ fontSize: '0.8rem' }}>
                                                                    <div>Occupied: <strong>{layout.occupied_slots}</strong></div>
                                                                    <div>Vacant: <strong>{layout.vacant_slots}</strong></div>
                                                                    <div>Total: <strong>{layout.total_slots}</strong></div>
                                                                </Popover.Body>
                                                            </Popover>
                                                        }
                                                    >
                                                        <button className="slots-popover-btn">{layout.slots} slots</button>
                                                    </OverlayTrigger>
                                                </div>
                                            ))
                                        ) : (
                                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>No layouts</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-center py-4 text-muted">No properties found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Summary stats table */}
            {Object.keys(propertyStats?.statistics?.byType || {}).length > 0 && (
                <div className="table-responsive stats-summary-table">
                    <table className="table mb-0">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Parking Areas</th>
                                <th>Total Slots</th>
                                <th>Occupied / Vacant</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(propertyStats.statistics.byType).map((type, index) => {
                                const { parkingAreas, totalSlots, occupiedSlots, vacantSlots } = propertyStats.statistics.byType[type];
                                return (
                                    <tr key={index}>
                                        <td>{type}</td>
                                        <td>{parkingAreas}</td>
                                        <td>{totalSlots}</td>
                                        <td>
                                            <span className="occ-badge occ me-1">{occupiedSlots} O</span>
                                            <span className="occ-badge vac">{vacantSlots} V</span>
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr>
                                <td>Overall</td>
                                <td>{propertyStats.statistics?.overall?.totalParkingAreas}</td>
                                <td>{propertyStats.statistics?.overall?.totalSlots}</td>
                                <td>
                                    <span className="occ-badge occ me-1">{propertyStats.statistics?.overall?.totalOccupied} O</span>
                                    <span className="occ-badge vac">{propertyStats.statistics?.overall?.totalVacant} V</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Parking Layout Setup | Rentdigicare</title>
                </MetaTags>
                <div className="container-fluid">
                    <Breadcrumbs title="Home" breadcrumbItem="Parking Layout Setup" />
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <CardHeader>
                                    {/* Stats + toggle row */}
                                    <div className="parking-stats-row">
                                        <div className="stat-chip">
                                            Total Properties <strong>{propertyStats.total}</strong>
                                        </div>
                                        <div className="stat-chip">
                                            Active <strong>{propertyStats.activeProperties}</strong>
                                        </div>
                                        <button
                                            className={`btn-parking btn-filter-toggle ms-auto ${isFilterOpen ? 'active' : ''}`}
                                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                                        >
                                            <i className={`mdi mdi-filter-${isFilterOpen ? 'off' : 'outline'}`} />
                                            {isFilterOpen ? 'Collapse Filters' : 'Expand Filters'}
                                        </button>
                                    </div>
                                    <div className="parking-filter-panel">
                                        <FilterSection />
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    <PropertiesTable />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <ParkingLayoutModal
                isOpen={modalState.isOpen}
                toggle={closeModal}
                property={modalState.property}
                onSubmit={handleParkingLayoutSubmit}
            />
        </React.Fragment>
    );
};

export { LayoutSetup };